import { ABOVE_HEAD, LINE } from "../nameplates/layout.ts";

/**
 * Where a ground item's name goes (P2.8-T76): one line in the pixel face's nameplate role, centred over the item and
 * standing `ABOVE_HEAD` logical px above its anchor, as a nameplate stands over a head. Every position is worked out
 * in device pixels from an anchor that is itself a whole device pixel, as a nameplate's is, so a label draws on the
 * device grid at every UI scale and never judders where the UI scale and the world zoom differ. Labels that would
 * cover each other are nudged up or down just enough to clear (`LabelStack`), so every name in a pile stays readable.
 */

/**
 * Logical pixels kept between two labels, across and up.
 */
export const LABEL_GAP = 1;

/**
 * A label's left edge.
 *
 * @param anchorX The device pixel the label is centred on, the item's point across.
 * @param scale The UI scale.
 * @param width The label's width in logical pixels.
 * @returns The left edge in device pixels.
 */
export function labelLeft(anchorX: number, scale: number, width: number): number {
  return anchorX - Math.floor((width * scale) / 2);
}

/**
 * A label's top edge before any stacking.
 *
 * @param anchorY The device pixel the label stands above.
 * @param scale The UI scale.
 * @returns The top edge in device pixels.
 */
export function labelTop(anchorY: number, scale: number): number {
  return anchorY - (ABOVE_HEAD + LINE.height) * scale;
}

/**
 * The placing of the labels shown, the way the action role-playing games the author named lay out their loot labels
 * (the author, 2026-10-10: "Its how PoE and Diablo did it."): each label stays by its item and, where it would cover
 * a label already placed, is nudged up or down just far enough that the two texts clear each other, the smallest nudge
 * winning and up winning a tie. Items themselves may overlap freely. Labels are taken lowest on screen first, then
 * leftmost, then by handle, so the placing is the same whatever order they arrive in. It is worked out only when which
 * labels show, the world zoom or the UI scale changes; the arrays grow when more labels show than ever before, and are
 * reused after.
 *
 * @example
 * stack.reset();
 * stack.add(41, left, top, width);
 * stack.solve(LINE.height * scale, LABEL_GAP * scale);
 * const shift = stack.shift(0);
 */
export class LabelStack {
  private handles = new Int32Array(16);
  private lefts = new Float64Array(16);
  private tops = new Float64Array(16);
  private widths = new Float64Array(16);
  private shifts = new Float64Array(16);
  private order = new Int32Array(16);
  private size = 0;

  /**
   * How many labels were added since the last reset.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.size;
  }

  /**
   * Forgets every label added.
   */
  public reset(): void {
    this.size = 0;
  }

  /**
   * Adds a label at its place before nudging.
   *
   * @param handle Its item's handle.
   * @param left Its left edge in device pixels.
   * @param top Its top edge in device pixels.
   * @param width Its width in device pixels.
   */
  public add(handle: number, left: number, top: number, width: number): void {
    if (this.size === this.handles.length) {
      this.grow();
    }
    const index = this.size;
    this.handles[index] = handle;
    this.lefts[index] = left;
    this.tops[index] = top;
    this.widths[index] = width;
    this.shifts[index] = 0;
    this.size += 1;
  }

  /**
   * How far a label is nudged, once solved.
   *
   * @param index The label's place in the order it was added.
   * @returns The nudge in device pixels, negative up and positive down, 0 for a label that keeps its place.
   */
  public shift(index: number): number {
    return this.shifts[index] as number;
  }

  /**
   * Places the labels added.
   *
   * @param height A label's height in device pixels.
   * @param gap The gap kept between labels in device pixels.
   */
  public solve(height: number, gap: number): void {
    const count = this.size;
    for (let index = 0; index < count; index += 1) {
      let at = index;
      while (at > 0 && this.before(index, this.order[at - 1] as number)) {
        this.order[at] = this.order[at - 1] as number;
        at -= 1;
      }
      this.order[at] = index;
    }
    for (let placed = 0; placed < count; placed += 1) {
      const label = this.order[placed] as number;
      const top = this.tops[label] as number;
      let best = 0;
      if (this.covers(label, placed, top, height, gap)) {
        best = Number.POSITIVE_INFINITY;
        for (let earlier = 0; earlier < placed; earlier += 1) {
          const other = this.order[earlier] as number;
          const otherTop = (this.tops[other] as number) + (this.shifts[other] as number);
          const up = otherTop - height - gap - top;
          const down = otherTop + height + gap - top;
          if (Math.abs(up) <= Math.abs(best) && !(Math.abs(up) === Math.abs(best) && best < 0) && !this.covers(label, placed, top + up, height, gap)) {
            best = up;
          }
          if (Math.abs(down) < Math.abs(best) && !this.covers(label, placed, top + down, height, gap)) {
            best = down;
          }
        }
      }
      this.shifts[label] = Number.isFinite(best) ? best : 0;
    }
  }

  /**
   * Whether a label at a top would cover any label placed before it, with the gap kept across and up.
   *
   * @param label The label's index.
   * @param placed How many labels are placed, in order.
   * @param top The top to try, in device pixels.
   * @param height A label's height in device pixels.
   * @param gap The gap in device pixels.
   * @returns True when it would cover one.
   */
  private covers(label: number, placed: number, top: number, height: number, gap: number): boolean {
    const left = this.lefts[label] as number;
    const right = left + (this.widths[label] as number);
    for (let earlier = 0; earlier < placed; earlier += 1) {
      const other = this.order[earlier] as number;
      const otherLeft = this.lefts[other] as number;
      const otherTop = (this.tops[other] as number) + (this.shifts[other] as number);
      if (left < otherLeft + (this.widths[other] as number) + gap && otherLeft < right + gap && top < otherTop + height + gap && otherTop < top + height + gap) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether one label is placed before another: lower on screen, then further left, then by handle.
   *
   * @param a One label's index.
   * @param b The other's.
   * @returns True when `a` goes first.
   */
  private before(a: number, b: number): boolean {
    const topA = this.tops[a] as number;
    const topB = this.tops[b] as number;
    if (topA !== topB) {
      return topA > topB;
    }
    const leftA = this.lefts[a] as number;
    const leftB = this.lefts[b] as number;
    if (leftA !== leftB) {
      return leftA < leftB;
    }
    return (this.handles[a] as number) < (this.handles[b] as number);
  }

  /**
   * Doubles every array, keeping what they hold.
   */
  private grow(): void {
    const size = this.handles.length * 2;
    const handles = new Int32Array(size);
    handles.set(this.handles);
    this.handles = handles;
    for (const key of ["lefts", "tops", "widths", "shifts"] as const) {
      const next = new Float64Array(size);
      next.set(this[key]);
      this[key] = next;
    }
    this.order = new Int32Array(size);
  }
}
